/**
 * Confetti — celebratory particle burst that fires when the user
 * finishes scratching the foil card.
 *
 * Trajectory (the "party vibe" the user asked for 2026-06-09):
 * pieces launch from the CENTER of the card with randomized burst
 * angles in the upper hemisphere (favoring up-and-out), reach a peak
 * around 30% of the way through their flight, then gravity pulls them
 * down with continued lateral drift. The two-phase motion (rapid
 * burst-out → slower fall) reads as an explosion rather than a
 * gentle shower.
 *
 * Math:
 *   - angle in [-160°, -20°] (radians) → upper hemisphere with
 *     up-and-out bias (-90° is straight up, -45° is up-right, etc.)
 *   - burst velocity 200–400 px → distance from center at peak
 *   - burstX = cos(angle) · velocity (horizontal component)
 *   - burstY = sin(angle) · velocity (vertical, negative = upward)
 *   - fallY  = burstY + (500–800) → gravity pulls down past start
 *   - fallX  = burstX ± (40–100)  → small additional lateral drift
 *
 * Per-piece values flow into CSS via inline custom properties
 * (--burst-x, --burst-y, --fall-x, --fall-y, --rotate-*). The
 * @keyframes itself is piece-agnostic — same animation drives every
 * piece, only the custom-prop values differ. Spread is memo-stable
 * via useMemo so re-renders don't reshuffle in-flight pieces.
 *
 * Performance: 36 pieces × CSS transform+opacity only. No JS
 * animation loop. Composited on GPU via translate3d. iOS Safari
 * handles 60fps easily.
 */
import { useMemo } from 'react';

interface Props {
  /** True once the engine has fired onScratchComplete. */
  active: boolean;
}

/** Confetti palette — brand gradient stops (sky / gold / coral) plus
 *  the indigo button color and an emerald. Five colors interleaved
 *  around 36 pieces gives ~7 pieces per color — cheerful coherent mix. */
const COLORS = ['#3EAAD6', '#FFC857', '#FF6B9D', '#6366F1', '#34D399'];

/** 36 pieces → enough density for a "party" feel without choking iOS
 *  Safari's compositor on older devices. Earlier 24-piece prototype
 *  felt sparse (user feedback 2026-06-09 "just released down rather
 *  than a party vibe"). */
const PIECE_COUNT = 36;

interface Piece {
  color: string;
  /** ms — stagger piece launches so the burst feels organic, not a
   *  uniform shower. Tighter window than the old straight-fall version
   *  so the burst peaks all happen close together (more "explosion" feel). */
  delay: string;
  /** ms — randomizes per-piece flight time so they don't all land
   *  at once. Slightly faster than the old fall version so the burst
   *  feels energetic. */
  duration: string;
  /** Initial rotation — so pieces don't all start at 0deg. */
  rotateStart: string;
  /** Intermediate rotation at burst peak (30% mark). Lots of spin
   *  between 0 and peak makes pieces look launched, not floating. */
  rotateMid: string;
  /** Final rotation by end of fall. Bigger total spin than rotateMid
   *  gives the "tumbling through the air" feel. */
  rotateEnd: string;
  /** px — horizontal distance from card center at burst peak. */
  burstX: string;
  /** px — vertical distance from card center at burst peak (negative = up). */
  burstY: string;
  /** px — final horizontal position (burstX + small additional drift). */
  fallX: string;
  /** px — final vertical position (well below card edge for clean exit). */
  fallY: string;
  /** Rectangular, square, or streamer — varies the silhouette so the
   *  confetti doesn't look like a uniform grid of dots. */
  shape: 'rect' | 'square' | 'streamer';
}

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function buildPieces(): Piece[] {
  return Array.from({ length: PIECE_COUNT }, (_, i) => {
    // Burst angle: upper hemisphere, biased up-and-out.
    // Standard math convention with CSS-Y-down:
    //   -180° = left, -90° = up, 0° = right, +90° = down
    // -160 to -20 covers the upper hemisphere minus the steep-up
    // and steep-side edges (avoids pieces shooting straight up only
    // to fall back to origin — that reads as "boring vertical").
    const angleRad = (rand(-160, -20) * Math.PI) / 180;
    const burstVel = rand(200, 400);

    const burstX = Math.cos(angleRad) * burstVel;
    const burstY = Math.sin(angleRad) * burstVel; // negative for upward

    // Fall position — burst position + gravity pull (always down,
    // hence positive Y add) + a small lateral drift to keep the
    // motion "weighty" rather than perfectly ballistic.
    const fallY = burstY + rand(550, 800);
    const fallX = burstX + (Math.random() < 0.5 ? -1 : 1) * rand(40, 100);

    // Shape mix — 1/2 rect, 1/3 square, 1/6 streamer. Streamers are
    // long thin pieces that look like party streamers/ribbons.
    const shapeRoll = i % 6;
    const shape: Piece['shape'] =
      shapeRoll === 0 ? 'streamer' : shapeRoll <= 2 ? 'square' : 'rect';

    return {
      color: COLORS[i % COLORS.length],
      delay: `${Math.round(rand(0, 220))}ms`,
      duration: `${Math.round(rand(1600, 2400))}ms`,
      rotateStart: `${Math.round(rand(0, 360))}deg`,
      rotateMid: `${Math.round(rand(360, 720))}deg`,
      rotateEnd: `${Math.round(rand(1080, 1800))}deg`,
      burstX: `${Math.round(burstX)}px`,
      burstY: `${Math.round(burstY)}px`,
      fallX: `${Math.round(fallX)}px`,
      fallY: `${Math.round(fallY)}px`,
      shape,
    };
  });
}

export function Confetti({ active }: Props) {
  // Memoize the piece spread so a re-render (e.g. from a scratchComplete
  // state flip causing parent re-render) doesn't reshuffle in-flight pieces.
  const pieces = useMemo(buildPieces, []);

  // Mounting the pieces only when active flips ensures the CSS animations
  // start fresh from t=0 (mounting is what kicks them off). If we always
  // rendered the children, opacity would be 0 but the CSS clock would
  // have advanced to wherever in the keyframe sequence we are at the
  // moment scratchComplete fires — pieces would jump in mid-flight.
  if (!active) return null;

  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <div
          key={i}
          className={`confetti-piece confetti-piece-${p.shape}`}
          style={{
            backgroundColor: p.color,
            animationDelay: p.delay,
            animationDuration: p.duration,
            // CSS custom properties referenced inside @keyframes
            // confetti-burst — keeps the keyframe itself piece-agnostic
            // and lets each piece carry its own randomized trajectory.
            ['--rotate-start' as string]: p.rotateStart,
            ['--rotate-mid' as string]: p.rotateMid,
            ['--rotate-end' as string]: p.rotateEnd,
            ['--burst-x' as string]: p.burstX,
            ['--burst-y' as string]: p.burstY,
            ['--fall-x' as string]: p.fallX,
            ['--fall-y' as string]: p.fallY,
          }}
        />
      ))}
    </div>
  );
}
